import {
  setupReturnCorrelationSchema,
  readCorrelatedSetupReturn,
  clearSetupReturnParams,
} from "components/payment/PaymentMethodSetup/setupReturn";
import * as yup from "yup";
import pick from "lodash/pick";
import type { CreateReservationFormState } from "../steps/confirm/components/CreateReservationForm/form/schemaValidation";
import type { CreateRequestState } from "./initialState";
import type { ReservationPaymentSetup } from "./ReservationPaymentSetupProvider";
import {
  profileSetupCorrelationSchema,
  type ProfileSetupCorrelation,
} from "components/profile/paymentMethodSetupReturn";

const FORM_FIELDS = [
  "driverGratuityPctCustomerInput",
  "pickUpVariantSelected",
  "promoCodeCustomerInput",
  "additonalItemsByTrip",
] as const;
export type ReservationFormSnapshot = Pick<
  CreateReservationFormState,
  (typeof FORM_FIELDS)[number]
>;

const STORAGE_KEY = "moovs.reservationPaymentMethodSetup";
const correlationSchema = setupReturnCorrelationSchema.shape({
  expiresAt: yup.number().required(),
  form: yup.mixed<ReservationFormSnapshot>().optional(),
  profileSetup: profileSetupCorrelationSchema.nullable().default(null),
  state: yup
    .mixed<ReservationPaymentSetup>()
    .required()
    .test(
      "checkout context",
      (value) =>
        !!value?.ownerKey && !!value.checkoutContext && !!value.attemptKey
    ),
  request: yup
    .mixed<CreateRequestState>()
    .required()
    .test(
      "checkout request",
      (value) =>
        !!value?.trip?.routes?.[0]?.vehicle?.id &&
        Array.isArray(value.trip.stops)
    ),
});
export type ReservationSetupReturn = {
  state: ReservationPaymentSetup;
  request: CreateRequestState;
};

export const retainReservationSetup = (
  state: ReservationPaymentSetup,
  request: CreateRequestState,
  setupIntentId: string,
  profileSetup?: ProfileSetupCorrelation,
  form?: CreateReservationFormState
) => {
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        returnPath: window.location.pathname,
        setupIntentId,
        profileSetup,
        form: form ? pick(form, FORM_FIELDS) : undefined,
        expiresAt: Date.now() + 30 * 60 * 1000,
        state: {
          ...state,
          attempt: undefined,
          result: undefined,
          resume: undefined,
          addCardReturn: undefined,
          restoredForm: undefined,
        },
        request,
      })
    );
  } catch {
    // Same-page checkout can continue without redirect recovery storage.
  }
};

export const clearReservationSetupReturn = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage cleanup must not reject a valid payment method.
  }
};

/** Read Stripe's secret into memory only, then scrub the URL before checkout effects run. */
export const readReservationSetupReturn = (): ReservationSetupReturn | null => {
  const correlation = readCorrelatedSetupReturn(STORAGE_KEY, correlationSchema);
  clearSetupReturnParams();
  if (!correlation || correlation.expiresAt <= Date.now()) return null;
  const { clientSecret } = correlation;
  const profile = correlation.profileSetup;
  if (
    profile &&
    (profile.returnPath !== correlation.returnPath ||
      profile.setupIntentId !== correlation.setupIntentId ||
      correlation.state.ownerKey !==
        JSON.stringify([
          profile.operatorId,
          "authenticated",
          profile.payerId,
        ]))
  )
    return null;
  return {
    request: correlation.request,
    state: {
      ...correlation.state,
      restoredForm: correlation.form
        ? pick(correlation.form, FORM_FIELDS)
        : undefined,
      ...(profile
        ? { addCardReturn: { ...profile, clientSecret } }
        : {
            resume: {
              setupIntentId: correlation.setupIntentId,
              clientSecret,
            },
          }),
    },
  };
};
